import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '../../../global/Button';
import { isNullOrEmpty } from '../../../utils/utils';
import { EVENT_TICKET_ALREADY_CLAIMED_ERROR } from '../../../utils/errors';
import {
  getConfigurations,
  getRemainingBrownTickets,
  signUpUserToBrownCampaign,
  subscribeUserToIterableList,
} from '../../../utils/service';
import Input from '../../../global/InputNew';
import Checkbox from '../../../global/Mui/Checkbox';
import FormControlLabel from '../../../global/Mui/FormControlLabel';
import LoadingBar from '../../../global/LoadingBar';
import StateSelector from '../../Register/Components/StateSelector';
import { getTrackerId, POSTHOG_CAPTURE_ATTRIBUTES, TRACKER_IDS } from '../../../config/tracker';

import './BrownForm.scss';

const {
  embed: {
    brownForm: {
      submitButton: trackerSubmitButton,
    },
  },
} = TRACKER_IDS;

const emailRegex = /^[a-zA-Z0-9._%+-]+@brown\.edu$/;
const brownWebsite = '{website}';

export default function BrownForm() {
  const intervalRef = useRef(undefined);
  const autocompleteInput = useRef(null);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [configData, setConfigData] = useState({});

  const [remainingTickets, setRemainingTickets] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [city, setCity] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedStateCode, setSelectedStateCode] = useState('');
  const [zip, setZip] = useState('');
  const [isOffCampusAddress, setIsOffCampusAddress] = useState(false);

  const isCampaignForm = useMemo(() => remainingTickets > 0, [remainingTickets]);

  const handlePlaceChanged = (autocompleteObj) => {
    const place = autocompleteObj.getPlace();
    setAddress1(place.name);
    setCity(
      place.address_components.find((component) => component.types.includes('locality'))?.long_name,
    );
    setSelectedState(
      place.address_components.find((component) => component.types.includes('administrative_area_level_1'))?.long_name,
    );
    setSelectedStateCode(
      place.address_components.find((component) => component.types.includes('administrative_area_level_1'))?.short_name,
    );

    const postalCode = place.address_components.find((component) => component.types.includes('postal_code'))?.long_name;
    const zipSuffix = place.address_components.find((component) => component.types.includes('postal_code_suffix'))?.long_name;
    const zipWithSuffix = zipSuffix ? `${postalCode}-${zipSuffix}` : postalCode;
    setZip(zipWithSuffix);
  };

  useEffect(() => {
    let autocompleteObj;
    if (!isInitialLoad) {
      autocompleteObj = new window.google.maps.places.Autocomplete(autocompleteInput.current, {
        types: ['address'],
        sessionToken: new window.google.maps.places.AutocompleteSessionToken(),
        componentRestrictions: { country: ['us'] },
      });

      autocompleteObj.setFields(['address_components', 'name']);
      autocompleteObj.addListener('place_changed', () => handlePlaceChanged(autocompleteObj));
    }

    return () => {
      if (autocompleteObj) {
        window.google.maps.event.clearInstanceListeners(autocompleteObj);
      }
    };
  }, [isInitialLoad]);

  const currentData = useMemo(
    () => (isCampaignForm ? configData.availableConfig : configData.missedConfig),
    [isCampaignForm, configData],
  );

  const commonFields = useMemo(
    () => [
      {
        id: 'firstName',
        placeholder: 'First name*',
        value: firstName,
        onChange: setFirstName,
      },
      {
        id: 'lastName',
        placeholder: 'Last name*',
        value: lastName,
        onChange: setLastName,
      },
      {
        id: 'email',
        placeholder: 'Enter your Brown University email*',
        value: email,
        onChange: setEmail,
        errorMessage:
          email && !emailRegex.test(email) ? 'Email must be a valid Brown University email' : '',
      },
    ],
    [firstName, lastName, email],
  );

  const campaignFields = useMemo(
    () => [
      {
        id: 'address1',
        placeholder: 'Address*',
        value: address1,
        onChange: setAddress1,
        inputRef: autocompleteInput,
      },
      {
        id: 'address2',
        placeholder: 'Apartment, Suite, etc. (optional)',
        value: address2,
        onChange: setAddress2,
      },
      {
        id: 'city',
        placeholder: 'City*',
        value: city,
        onChange: setCity,
      },
    ],
    [address1, address2, city],
  );

  useEffect(() => {
    const originalBackgroundColor = document.body.style.backgroundColor;
    document.body.style.backgroundColor = 'transparent';

    return () => {
      document.body.style.backgroundColor = originalBackgroundColor;
    };
  }, []);

  const isFormValid = () => {
    const commonFieldsAreValid =
      !isNullOrEmpty(firstName) &&
      !isNullOrEmpty(lastName) &&
      !isNullOrEmpty(email) &&
      emailRegex.test(email);

    const isAddresValid =
      !isNullOrEmpty(address1) &&
      !isNullOrEmpty(city) &&
      !isNullOrEmpty(selectedState) &&
      !isNullOrEmpty(selectedStateCode) &&
      !isNullOrEmpty(zip);

    return commonFieldsAreValid && (!isCampaignForm || isAddresValid);
  };

  const getTicketsInfo = useCallback(async () => {
    const res = await getRemainingBrownTickets();
    if (!res.error && res.data) {
      const { availableTickets } = res.data;
      setRemainingTickets(availableTickets);

      if (availableTickets === 0) {
        setErrorMsg('');
        clearInterval(intervalRef.current);
      }
    } else {
      // Switch to discount form
      setRemainingTickets(0);
    }
  }, []);

  const getInitialData = useCallback(async () => {
    const { value, error } = await getConfigurations('brownUniversityClaimBag');
    await getTicketsInfo();

    if (!error) {
      setConfigData(value);
    }

    setIsInitialLoad(false);
  }, []);

  useEffect(() => {
    getInitialData();

    intervalRef.current = setInterval(() => {
      getTicketsInfo();
    }, 5000);

    return () => clearInterval(intervalRef.current);
  }, []);

  const handleCampaignSubmit = async () => {
    setIsLoading(true);
    setErrorMsg('');

    const addressDetails = {
      address1,
      address2,
      city,
      provinceCode: selectedStateCode,
      zip,
      isOffCampusAddress,
    };

    if (isCampaignForm) {
      const res = await signUpUserToBrownCampaign({
        holderEmail: email,
        holderDetails: {
          firstName,
          lastName,
          ...addressDetails,
        },
      });

      if (res.error) {
        setErrorMsg(
          res.error.code === EVENT_TICKET_ALREADY_CLAIMED_ERROR
            ? 'This email address has already claimed a Take Back Bag'
            : res.error.message,
        );
        setIsLoading(false);
        return;
      }
    }

    const iterableRes = await subscribeUserToIterableList({
      listId: currentData.iterableListId,
      email,
      firstName,
      lastName,
      address: isCampaignForm ? addressDetails : {},
    });

    if (!iterableRes.error) {
      window.top.location.href = currentData.successUrl;
    } else {
      setErrorMsg(iterableRes.error.message);
    }

    setIsLoading(false);
  };

  return (
    <div className="BrownForm">
      {isInitialLoad || !currentData ? (
        <LoadingBar />
      ) : (
        <>
          <h1 className="BrownForm__title">
            {currentData.title
              .replace('{count}', remainingTickets)
              .replace('{plural}', remainingTickets === 1 ? '' : 's')}
          </h1>
          {currentData.description && currentData.description.includes(brownWebsite) ? (
            <p className="BrownForm__description">
              {currentData.description.split(brownWebsite)[0]}
              <a
                className="BrownForm__brownLink"
                href={currentData?.sustainabilityUrl || ''}
                target="_blank"
                rel="noopener noreferrer"
              >
                website
              </a>
              {currentData.description.split(brownWebsite)[1]}
            </p>
          ) : (
            <p className="BrownForm__description">{currentData.description}</p>
          )}
          <form className="BrownForm__form">
            {commonFields.map((inputField) => (
              <Input
                key={inputField.id}
                elementClassName="inputText"
                className="input"
                type="text"
                {...inputField}
              />
            ))}
            {isCampaignForm && (
              <>
                {campaignFields.map((inputField) => (
                  <Input
                    key={inputField.id}
                    elementClassName="inputText"
                    className="input"
                    type="text"
                    {...inputField}
                  />
                ))}
                <StateSelector
                  selectedState={selectedState}
                  setSelectedState={setSelectedState}
                  selectedStateCode={selectedStateCode}
                  setSelectedStateCode={setSelectedStateCode}
                />
                <Input
                  key="zip"
                  elementClassName="inputText"
                  className="input"
                  type="text"
                  placeholder="Zip*"
                  value={zip}
                  onChange={setZip}
                />
                <p className="BrownForm__form--instruction">Please, use your on-campus address.</p>
                <FormControlLabel
                  label="Check here if this is an off-campus address"
                  control={(
                    <Checkbox
                      value="isOffCampusAddress"
                      checked={isOffCampusAddress}
                      onChange={() => setIsOffCampusAddress((prev) => !prev)}
                      inputProps={{ 'aria-label': 'Check here if this is an off-campus address' }}
                    />
                  )}
                />
                <p className="BrownForm__form--instruction">
                  A bag will be delivered to your campus mailbox
                  (for both on- and off-campus students).
                </p>
              </>
            )}
            {errorMsg && <p className="BrownForm__form--error">{errorMsg}</p>}
            <Button
              className="BrownForm__form--submit"
              type={BUTTON_TYPE.PRIMARY}
              size={BUTTON_SIZE.LARGE}
              disabled={!isFormValid() || isLoading}
              onClick={handleCampaignSubmit}
              trackerProps={{
                [POSTHOG_CAPTURE_ATTRIBUTES.TRACKER_ID]: getTrackerId(trackerSubmitButton),
              }}
            >
              {currentData.buttonText}
            </Button>
          </form>
          <p className="BrownForm__footer">{currentData.footerMessage}</p>
        </>
      )}
    </div>
  );
}
